import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Users, Shield, ShieldCheck, Crown, Search, Loader2 } from "lucide-react";

const ROLE_META = {
  admin: { label: "Admin", color: "#FF2A55", icon: Crown },
  moderator: { label: "Modder", color: "#00F0FF", icon: ShieldCheck },
  user: { label: "Reporter", color: "#64748B", icon: Shield },
};

export const TeamManager = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");

  const load = useCallback(async (q = "") => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users", { params: q ? { search: q } : {} });
      setUsers(data);
    } catch { /* not admin or error */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const t = setTimeout(() => load(search), 300);
    return () => clearTimeout(t);
  }, [search, load]);

  if (user?.role !== "admin") return null;

  const setRole = async (u, role) => {
    setBusy(u.user_id);
    try {
      const { data } = await api.patch("/admin/users/role", { user_id: u.user_id, role });
      setUsers((us) => us.map((x) => (x.user_id === data.user_id ? data : x)));
      toast.success(`${data.name} is now ${ROLE_META[role].label}.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update role.");
    } finally { setBusy(""); }
  };

  return (
    <div className="animate-fade-up mb-8 rounded-2xl border border-[#FF2A55]/30 bg-[#0F1424] p-6" data-testid="team-manager">
      <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-white"><Users size={18} className="text-[#FF2A55]" /> Modder Team</h2>
      <p className="mt-1 text-sm text-[#94A3B8]">Promote trusted players to moderators so they can vet reports, or remove access.</p>

      <div className="relative mt-4 max-w-sm">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
        <Input data-testid="team-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name or email…" className="border-[#1E293F] bg-[#141B2D] pl-9 text-white placeholder:text-[#64748B]" />
      </div>

      {loading ? (
        <div className="flex justify-center py-8 text-[#64748B]"><Loader2 className="animate-spin" /></div>
      ) : (
        <div className="mt-4 space-y-2">
          {users.map((u) => {
            const meta = ROLE_META[u.role] || ROLE_META.user;
            const isSelf = u.user_id === user.user_id;
            return (
              <div key={u.user_id} data-testid={`team-row-${u.user_id}`} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-[#1E293F] bg-[#141B2D] px-4 py-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="truncate font-semibold text-white">{u.name}</span>
                    <span className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-bold uppercase" style={{ color: meta.color, backgroundColor: `${meta.color}18` }}>
                      <meta.icon size={10} /> {meta.label}
                    </span>
                    {isSelf && <span className="text-[10px] text-[#64748B]">(you)</span>}
                  </div>
                  <div className="truncate text-xs text-[#64748B]">{u.email}</div>
                </div>
                {!isSelf && u.role !== "admin" && (
                  <div className="flex gap-2">
                    {u.role === "moderator" ? (
                      <Button data-testid={`demote-${u.user_id}`} size="sm" variant="outline" disabled={busy === u.user_id} onClick={() => setRole(u, "user")} className="border-[#1E293F] bg-[#0F1424] text-[#94A3B8] hover:bg-[#1A233A] hover:text-white">
                        Remove Modder
                      </Button>
                    ) : (
                      <Button data-testid={`promote-${u.user_id}`} size="sm" disabled={busy === u.user_id} onClick={() => setRole(u, "moderator")} className="bg-[#00F0FF] font-bold text-[#090C15] hover:bg-[#00F0FF]/90">
                        {busy === u.user_id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ShieldCheck size={14} className="mr-1" /> Make Modder</>}
                      </Button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          {users.length === 0 && <p className="py-4 text-center text-sm text-[#64748B]">No users found.</p>}
        </div>
      )}
    </div>
  );
};
